var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import layoutComponentsDictionary from "../layoutComponentsDictionary";
import { LayoutComponentDisplay } from "../layoutComponentDisplay";
import copy from "fast-copy";
import { FormSettingsContext, getColorStyle, getLayoutStyle, getStyleSpacing, scrollToEl, } from "../../utils";
import { FormComponentButtons } from "../../formComponentButtons";
import "./settings";
import "./list";
import { LayoutButtons } from "../../layoutButtons";
import { BasicComponents } from "../../basicComponent";
/**
 * <b>Composant de rendu des dispositions d'affichages `layout-columns`</b>
 *
 * @param param0
 */
export function LayoutColumns(_a) {
    var _b;
    var module = _a.module, viewType = _a.viewType, fieldDefs = _a.fieldDefs, record = _a.record, fetchedRecord = _a.fetchedRecord, layouts = _a.layouts, onHandleUpdate = _a.onHandleUpdate, validations = _a.validations, isStudioField = _a.isStudioField, isSelected = _a.isSelected, layoutParent = _a.layoutParent, viewScreen = _a.viewScreen;
    var blocElement = useMemo(function () { return layouts[0] || {}; }, [layouts]);
    var childElements = useMemo(function () { return blocElement.components || []; }, [blocElement]);
    var childIndex = 0;
    var _c = useState([]), editedRecord = _c[0], setEditedRecord = _c[1];
    useEffect(function () {
        setEditedRecord(Object.keys(record).length === 0
            ? createNewRecord(fieldDefs)
            : copy(record));
    }, [record]);
    var updateRecord = function (edited) {
        setEditedRecord(edited);
        onHandleUpdate && onHandleUpdate(edited);
    };
    // Css pour le studio
    var borderStyle = isStudioField
        ? "border border-gray-500 border-dashed hover:border-red-500"
        : "";
    var _d = useContext(FormSettingsContext), settings = _d.settings, editedSettings = _d.editedSettings;
    var refs = useRef(null);
    var copyEditedValueFromProps = function () {
        var editedValue = copy(blocElement);
        var s = editedValue.settings || {};
        var styles = s.styles || {};
        var spacing = styles.spacing || {};
        var color = styles.color || {};
        var sizing = styles.sizing || {};
        styles.color = color;
        styles.spacing = spacing;
        styles.sizing = sizing;
        s.styles = styles;
        editedValue.settings = s;
        return editedValue;
    };
    // clique sur le composant
    var onClickComponent = function (e) {
        e.stopPropagation();
        editedSettings(__assign(__assign({}, settings), { selected: blocElement.id, clipBoard: settings.clipBoard, parentId: layoutParent }));
        scrollToEl(refs && (refs === null || refs === void 0 ? void 0 : refs.current));
    };
    // si composant séléctionné
    useEffect(function () {
        isStudioField &&
            settings.selected === blocElement.id &&
            editedSettings(__assign(__assign({}, settings), { target: blocElement.id, type: blocElement.type, component: childElements, selected: blocElement.id, parentId: layoutParent, currentComponent: copyEditedValueFromProps() }));
        isStudioField && copyEditedValueFromProps();
    }, [isStudioField && settings.selected]);
    var elementsWidths = function () {
        var s = blocElement.settings || {};
        var styles = s.styles || {};
        var sizes = styles.sizing || "";
        var stringWidth = sizes.width || "";
        var components = blocElement.components || [];
        var widths = [];
        widths = stringWidth
            .split(" ")
            .map(function (w) { return w.trim(); })
            .filter(function (item) { return item !== ""; });
        if (widths.length === 0) {
            widths.push("".concat(100.0 / (components.length || 1), "%"));
        }
        // Complete with last param if components are many than given width params
        if (widths.length > 0 && widths.length < components.length) {
            for (var i = 0; i < components.length - widths.length; i++) {
                widths.push(widths[widths.length - 1]);
            }
        }
        return widths;
    };
    var _e = useState(""), sizeStyle = _e[0], setSizeStyle = _e[1];
    var elementsStyles = function () {
        return elementsWidths().map(function (width) { return width; });
    };
    // Styles
    var _f = useState(), styles = _f[0], setStyles = _f[1];
    useEffect(function () {
        var _a, _b;
        setSizeStyle(elementsStyles());
        var spacingStyle = (blocElement.settings && __assign(__assign({}, getStyleSpacing(((_a = blocElement.settings) === null || _a === void 0 ? void 0 : _a.styles) || {})), getColorStyle(((_b = blocElement.settings) === null || _b === void 0 ? void 0 : _b.styles) || {}))) ||
            "";
        setStyles(__assign({}, spacingStyle));
    }, [(_b = blocElement.settings) === null || _b === void 0 ? void 0 : _b.styles]);
    return (_jsx(_Fragment, { children: _jsxs("div", __assign({ className: "flex flex-col w-full relative", onClick: function (v) {
                isStudioField && onClickComponent(v);
            }, style: getLayoutStyle(isSelected === blocElement.id), ref: function (el) {
                if (el)
                    refs.current = el;
            }, id: blocElement.id }, { children: [_jsx("div", __assign({ className: "flex flex-row space-x-4 ".concat(borderStyle), style: styles }, { children: childElements.map(function (c, index) {
                        var _a;
                        var fieldType = c.type || "";
                        // Ensure sizeStyle is an array, otherwise fallback to an empty array
                        var sizeValues = (Array.isArray(sizeStyle) ? sizeStyle : []).map(function (size) {
                            if (typeof size === "string") {
                                if (size.includes("/")) {
                                    var _a = size
                                        .split("/")
                                        .map(Number), numerator = _a[0], denominator = _a[1];
                                    return denominator
                                        ? numerator / denominator
                                        : parseFloat(size);
                                }
                                else if (size.includes("%")) {
                                    return parseFloat(size) / 100;
                                }
                            }
                            return parseFloat(size);
                        });
                        // Find the index of the maximum size
                        var maxIndex = sizeValues.indexOf(Math.max.apply(Math, sizeValues));
                        return (_jsx("div", __assign({ className: "".concat(((_a = sizeStyle[index]) === null || _a === void 0 ? void 0 : _a.includes("%"))
                                ? "w-[".concat(sizeStyle[index] || "100%", "]")
                                : "w-".concat(sizeStyle[index] || "100%"), " ").concat(index === maxIndex ? "flex-grow" : "") }, { children: (fieldType && fieldType === "etc-field") ||
                                fieldType === "header" ||
                                fieldType === "etc-widget" ? (_jsx(_Fragment, { children: _jsx(BasicComponents, { record: editedRecord, layoutType: fieldType, meta: c || [], fieldDefs: fieldDefs, validations: validations, isStudioField: isStudioField, isSelected: isSelected, layoutParent: blocElement && blocElement, viewScreen: viewScreen, onHandleUpdate: updateRecord, moduleName: module || "Accounts" }) })) : (_jsx(_Fragment, { children: _jsx("div", __assign({ className: "relative" }, { children: _jsx(LayoutComponentDisplay, { module: module, viewType: viewType, fieldDefs: fieldDefs, record: editedRecord, fetchedRecord: fetchedRecord, layouts: c, onHandleUpdate: updateRecord, validations: validations, isStudioField: isStudioField, isSelected: isSelected, layoutParent: blocElement, viewScreen: viewScreen }, "layout_".concat(c.settings && c.settings.type, "_").concat(childIndex++)) })) })) }), "element_".concat(c.settings && c.settings.type, "_").concat(childIndex++)));
                    }) })), isStudioField && (_jsxs(_Fragment, { children: [_jsx(FormComponentButtons, { parentId: layoutParent, meta: (settings && settings) || [], isLayout: true, isSelected: isSelected === blocElement.id }), _jsx(LayoutButtons, { parentId: layoutParent, component: blocElement || [], isLayout: true })] }))] })) }));
}
/**
 * Création de nouveau enregistrement
 * @param r
 * @param fieldDefs
 * @returns
 */
function createNewRecord(fieldDefs) {
    var record = {};
    var fields = Object.keys(fieldDefs);
    fields.forEach(function (field) {
        record[field] = "";
    });
    return record;
}
layoutComponentsDictionary.registerLayoutComponent("layout-columns", LayoutColumns);
